import React, { useEffect, useRef, useState } from "react";
import io from "socket.io-client";
import { useSelector } from "react-redux";
import { helpers, storage } from "../../../services";
import EntityForm from "@/modules/entity/forms";
import TaskMessageForm from "./TaskForm";
import { get } from "lodash";
import Update from "./components/update";
import { Tabs } from "antd";
import "./style.scss";
import { ArrowRightIcon, EditIcon } from "lucide-react";
import { Timeline } from "antd";

const Chat = ({ selected }) => {
  const storageToken = storage.get("token");
  const profile = useSelector((state) => state.auth.user);
  const [replyTo, setReplyTo] = useState(null);
  const [status, setStatus] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");

  const [tabKey, setTabKey] = useState("1");
  const [messages, setMessages] = useState([]);
  const [taskMessages, setTaskMessages] = useState([]);
  const [taskHistories, setTaskHistories] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [newTaskMessage, setNewTaskMessage] = useState("");
  const [openTaskTab, setOpenTaskTab] = useState(null);
  const socket_organization_id = selected && selected.id;

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState("note");
  const chatContainerRef = useRef(null);
  const formRef = useRef(null);

  const sendMessage = async () => {
    if (!newMessage.trim()) return;

    const hasReply = replyTo !== null && replyTo !== undefined;
    const isTaskReply = hasReply && replyTo.task && replyTo.task.id;
    const taskId = isTaskReply ? replyTo.task.id : null;

    const payload = {
      content: newMessage,
    };

    if (hasReply) {
      if (isTaskReply) {
        payload.task_id = taskId;
      } else if (replyTo.id) {
        payload.reply_to = replyTo.id;
      }

      if (status) {
        payload.status = parseInt(status);
      }

      if (scheduledDate) {
        payload.scheduled_time = scheduledDate;
      }

      if (durationMinutes) {
        payload.duration_minutes = parseInt(durationMinutes);
      }
    }

    try {
      const url = isTaskReply
        ? `https://api-kafe.zim-zim.uz/api/v1/app/employee/tasks/${taskId}`
        : `https://api-kafe.zim-zim.uz/api/v1/app/employee/chats?modelable_type=lead&modelable_id=${selected.id}`;

      const response = await fetch(url, {
        method: isTaskReply ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storageToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);

      if (response.ok) {
        setNewMessage("");
        setReplyTo(null);
        setStatus("");
        setScheduledDate("");
        setDurationMinutes("");

        if (isTaskReply) {
          setMessages((prevMessages) =>
            prevMessages.map((msg) => {
              if (msg.id === replyTo.id) {
                return {
                  ...msg,
                  task: {
                    ...msg.task,
                    status: parseInt(status),
                    scheduled_time: scheduledDate,
                    duration_minutes: parseInt(durationMinutes),
                  },
                };
              }
              return msg;
            }),
          );
        } else {
          const newMsg = result && (result.data || result);
          if (newMsg && newMsg.id) {
            setMessages((prevMessages) =>
              prevMessages.some((m) => m.id === newMsg.id)
                ? prevMessages
                : [...prevMessages, newMsg],
            );
          }
        }

        setTimeout(() => {
          const chatContainer = chatContainerRef.current;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
      } else {
        console.error("Failed to send message:", response.statusText);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };
  const sendTaskMessage = async () => {
    if (!newTaskMessage.trim()) return;

    const payload = {
      content: newTaskMessage,
    };

    try {
      const url = `https://api-kafe.zim-zim.uz/api/v1/app/employee/chats?task_id=${openTaskTab.id}&modelable_type=lead&modelable_id=${selected.id}`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${storageToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json().catch(() => null);

      if (response.ok) {
        setNewTaskMessage("");

        const newMsg = result && (result.data || result);
        if (newMsg && newMsg.id) {
          setTaskMessages((prevMessages) =>
            prevMessages.some((m) => m.id === newMsg.id)
              ? prevMessages
              : [...prevMessages, newMsg],
          );
        }

        setTimeout(() => {
          const chatContainer = chatContainerRef.current;
          if (chatContainer) {
            chatContainer.scrollTop = chatContainer.scrollHeight;
          }
        }, 100);
      } else {
        console.error("Failed to send message:", response.statusText);
      }
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const getMessages = async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);

    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const prevScrollHeight = chatContainer.scrollHeight;
    const prevScrollTop = chatContainer.scrollTop;

    try {
      const response = await fetch(
        `https://api-kafe.zim-zim.uz/api/v1/app/employee/chats?modelable_type=lead&modelable_id=${
          selected.id
        }&per_page=${20}&page=${page}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${storageToken}`,
          },
        },
      );
      const data = await response.json();
      if (data && data.data.length > 0) {
        setIsLoading(false);
        setMessages((prev) => [...data.data.reverse(), ...prev]);
        setPage((prevPage) => {
          return prevPage + 1;
        });
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
      setIsLoading(false);
    }
    setTimeout(() => {
      chatContainer.scrollTop =
        chatContainer.scrollHeight - prevScrollHeight + prevScrollTop;
    }, 0);
  };

  const getTaskHistories = async (taskId) => {
    try {
      const response = await fetch(
        `https://api-kafe.zim-zim.uz/api/v1/app/employee/tasks/${taskId}?modelable_type=lead&modelable_id=${selected.id}&include=histories.user,messages.user`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${storageToken}`,
          },
        },
      );
      const data = await response.json();
      if (data) {
        setIsLoading(false);
        setTaskMessages(data.messages);
        setTaskHistories(data.histories);
      } else {
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    } finally {
    }
  };

  useEffect(() => {
    const socket = io("wss://socket-kafe.zim-zim.uz", {
      transports: ["websocket"],
      extraHeaders: { Authorization: `Bearer ${storageToken}` },
      query: {
        modelable_type: "lead",
        modelable_id: selected.id,
        organization_id: null,
      },
    });

    socket.on("connect", () => {
      console.log("Connected to WebSocket server");
    });

    socket.on("disconnect", (reason) => {
      console.warn("Socket disconnected:", reason);
    });

    socket.on("connect_error", (error) => {
      console.error("Connection Error:", error);
    });

    socket.on("chatMessage", (message) => {
      console.log(message, "testmessage modelable_id");
      if (message && message.item) {
        if (
          message.modelable_type === "lead" &&
          Number(message.modelable_id) === selected.id
        ) {
          setMessages((prevMessages) =>
            prevMessages.some((m) => m.id === message.item.id)
              ? prevMessages
              : [...prevMessages, message.item],
          );
          setTimeout(() => {
            const chatContainer = chatContainerRef.current;
            if (chatContainer) {
              chatContainer.scrollTop = chatContainer.scrollHeight;
            }
          }, 100);
        }
      }
    });

    return () => {
      socket.off("message");
      socket.disconnect();
    };
  }, [socket_organization_id, storageToken]);

  useEffect(() => {
    if (!socket_organization_id) return;
    setPage(1);
    setHasMore(true);
    getMessages();
  }, []);

  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;
    let isFetching = false;
    const handleScroll = () => {
      if (
        chatContainer.scrollTop === 0 &&
        hasMore &&
        !isLoading &&
        !isFetching
      ) {
        isFetching = true;
        getMessages().finally(() => (isFetching = false));
      }
    };

    chatContainer.addEventListener("scroll", handleScroll);
    return () => chatContainer.removeEventListener("scroll", handleScroll);
  }, [hasMore, isLoading, page]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isInsideForm =
        formRef.current && formRef.current.contains(event.target);
      const isInsideDatepicker =
        document.querySelector(".react-datepicker") &&
        document.querySelector(".react-datepicker").contains(event.target);
      if (!isInsideForm && !isInsideDatepicker) {
        closeCardForm();
      }
    };

    if (replyTo) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [replyTo]);

  const handleSelectChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const closeCardForm = () => {
    setTabKey("1");
    setStatus("");
    setScheduledDate("");
    setDurationMinutes("");
  };

  const timelineItems = taskHistories.map((item) => ({
    color: item.status,
    children: (
      <>
        <p
          style={{
            fontWeight: "bold",
            margin: "0",
            marginTop: "5px",
          }}
        >
          {item.user ? item.user.name : ""}{" "}
          <i
            style={{
              fontWeight: "lighter",
              margin: 0,
            }}
          >
            ({helpers.formatDate(item.created_at, "DD.MM.YYYY / HH:mm")})
          </i>
        </p>
        {item.changes.map((change) => (
          <>
            <p style={{ fontWeight: "bold", margin: 0 }}>{change.title}</p>
            <p
              style={{
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {change.old_value ? change.old_value : "--"}
              <ArrowRightIcon color={"green"} width={20} height={20} />{" "}
              {change.new_value ? change.new_value : "--"}
            </p>
          </>
        ))}
        <i
          style={{
            margin: "0",
            fontSize: "12px",
          }}
        >
          {item.description}
        </i>
      </>
    ),
  }));

  const items = [
    {
      key: "1",
      label: "История задачи",
      children: (
        <div className={"task-timeline"}>
          <Timeline variant="filled" items={timelineItems} />
        </div>
      ),
    },
    {
      key: "2",
      label: "Чат",
      children: (
        <>
          <div className="leads-chat-container-task">
            <div className="--loading" />
            <div className="chat-messages-task" ref={chatContainerRef}>
              {isLoading && <div className="--loading-dark" />}
              {taskMessages.map((msg, index) => {
                const isTaskMessage = msg.id;
                const taskData = isTaskMessage ? msg : null;

                return (
                  <div
                    key={index}
                    className={`chat-bubble ${
                      profile.id == msg.user_id ? "sent" : "received"
                    } ${replyTo &&
                      replyTo.id === msg.id &&
                      replyTo.task_id &&
                      "active-card"}`}
                  >
                    <div className="context">
                      <p
                        style={{
                          color: "#ffc500",
                          display: "block",
                          margin: 0,
                          paddingLeft: "10px",
                        }}
                      >
                        {msg.user ? msg.user.name : ""}
                      </p>
                      <b style={{ color: "#ffc500" }}>{msg.content || ""}</b>
                      <div className="timestamp">
                        {helpers.formatDate(
                          msg.created_at,
                          "DD.MM.YYYY / HH:mm",
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="chat-input-task">
              <div className="note-input">
                <div className="!flex">
                  <textarea
                    value={newTaskMessage}
                    onChange={(e) => setNewTaskMessage(e.target.value)}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        sendTaskMessage();
                      }
                    }}
                    placeholder="Введите сообщение..."
                    rows={1}
                  />
                  <button
                    className="send-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      sendTaskMessage();
                    }}
                  >
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      ),
    },
    {
      key: "3",
      label: "",
      children: (
        <div ref={formRef} className="reply-section ">
          <Update
            leadId={selected.id}
            selected={openTaskTab}
            closeCardForm={closeCardForm}
            onSuccess={(updatedTask) => {
              closeCardForm();
              getTaskHistories(openTaskTab.id);
            }}
          />
        </div>
      ),
    },
  ];

  const onChange = (key) => {
    setTabKey(key);
  };

  return (
    <div className="leads-chat-container">
      <div className="modal-title !flex !justify-between !items-center">
        <p>{selected && selected.customer_name}</p>
        <div className="input-controls">
          <select value={selectedOption} onChange={handleSelectChange}>
            <option value="note">Chat</option>
            <option value="task">Задача</option>
          </select>
        </div>
      </div>
      <div className="--loading" />
      <div className="chat-messages" ref={chatContainerRef}>
        {isLoading && <div className="--loading-dark" />}
        {messages.map((msg, index) => {
          const isTask = msg.type === "task";
          const isCall = msg.type === "call";
          const isCallStatus = isCall && msg.status;

          const isTaskMessage = msg.id && msg.type === "task";
          const taskData = isTaskMessage ? msg : null;

          return (
            <div
              key={index}
              className={`chat-bubble ${
                profile.id == msg.user_id ? "sent" : "received"
              } ${replyTo &&
                replyTo.id === msg.id &&
                replyTo.task_id &&
                "active-card"}
                            ${isTask ? "task" : isCall ? "call" : "message"}
                            `}
            >
              <div className="context">
                {!isTask && !isCall && (
                  <p
                    style={{
                      color: "#ffc500",
                      display: "block",
                      margin: 0,
                      paddingLeft: "10px",
                    }}
                  >
                    {msg.user_name}
                  </p>
                )}

                {isCall ? (
                  <div
                    className={`call-card ${
                      msg.status === 1 ? "answered" : "missed"
                    }`}
                  >
                    <div className="call-meta">
                      <span className="call-user">{msg.user_name}</span>
                      <span className="call-time">
                        {helpers.formatDate(
                          msg.created_at,
                          "DD.MM.YYYY / HH:mm",
                        )}
                      </span>
                    </div>
                    <div className="call-header">
                      <span className="call-icon">
                        {msg.status === 1 ? "📞" : "📵"}
                      </span>
                      <span className="call-label">
                        {msg.status === 1
                          ? "Звонок (отвечен)"
                          : "Пропущенный звонок"}
                      </span>
                    </div>
                    <div
                      className="call-content"
                      dangerouslySetInnerHTML={{ __html: msg.content || "" }}
                    />
                    {msg.status === 1 && msg.recording && (
                      <audio
                        controls
                        src={msg.recording}
                        className="call-audio"
                      />
                    )}
                  </div>
                ) : (
                  <b style={{ color: "#ffc500" }}>{msg.content || ""}</b>
                )}

                {isTask && (
                  <>
                    <div
                      className={`task-content`}
                      onClick={(e) => {
                        if (isTask) {
                          if (openTaskTab) {
                            if (openTaskTab.id === msg.id) {
                              setOpenTaskTab(null);
                            } else {
                              setOpenTaskTab(msg);
                            }
                          } else {
                            setOpenTaskTab(msg);
                          }
                          getTaskHistories(msg.id);
                        }
                      }}
                    >
                      <>
                        <div className="task-title">
                          <div className="deadline">
                            {taskData.status === 10 ? (
                              <>
                                <strong>Завершено:</strong>{" "}
                                {taskData.scheduled_time
                                  ? helpers.formatDate(
                                      taskData.updated_at,
                                      "DD.MM.YYYY / HH:mm",
                                    )
                                  : "---"}
                              </>
                            ) : (
                              <>
                                <strong>Дедлайн:</strong>{" "}
                                {taskData.scheduled_time
                                  ? helpers.formatDate(
                                      taskData.scheduled_time,
                                      "DD.MM.YYYY",
                                    )
                                  : "—"}
                              </>
                            )}
                            {(() => {
                              const serviceName =
                                typeof taskData.service === "string"
                                  ? taskData.service
                                  : get(taskData, "service.name", "");
                              return (
                                serviceName && (
                                  <div className="flex gap-2">
                                    <strong>Услуга:</strong>{" "}
                                    <p>{serviceName}</p>
                                  </div>
                                )
                              );
                            })()}
                          </div>
                        </div>

                        <div className="task-details">
                          {taskData.user_name ? (
                            <div className={"!flex !items-center gap-2"}>
                              <strong>Ответственный:</strong>{" "}
                              <p>{taskData.user_name}</p>
                            </div>
                          ) : (
                            "---"
                          )}

                          <div>
                            <strong>Статус:</strong>{" "}
                            <strong>
                              {taskData.status === 1
                                ? "Новые"
                                : taskData.status === 2
                                ? "В процессе"
                                : taskData.status === 10
                                ? "Завершенный"
                                : taskData.status === 11
                                ? "Отсроченный"
                                : taskData.status === 12
                                ? "Продленный"
                                : taskData.status === 20
                                ? "Отменено"
                                : "Неизвестно"}
                            </strong>
                          </div>
                        </div>
                      </>
                    </div>
                    {openTaskTab && openTaskTab.id === taskData.id && (
                      <div className="task-tabs">
                        <Tabs
                          defaultActiveKey={"1"}
                          activeKey={tabKey}
                          items={items}
                          onChange={onChange}
                        />
                        <EditIcon
                          className={`edit-icon ${
                            tabKey === "3" ? "active" : ""
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setTabKey("3");
                          }}
                        />
                      </div>
                    )}
                  </>
                )}

                {!isCall && (
                  <div className="timestamp">
                    {helpers.formatDate(msg.created_at, "DD.MM.YYYY / HH:mm")}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <div className="chat-input">
        {selectedOption === "note" ? (
          <div className="note-input">
            <div className="!flex">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                placeholder="Введите сообщение..."
                rows={2}
              />
              <button className="send-button" onClick={sendMessage}>
                Send
              </button>
            </div>
          </div>
        ) : (
          <div className="task-input">
            <EntityForm.Main
              method="post"
              entity="tasks"
              name={`all`}
              normalizeData={(data) => data}
              prependData={true}
              url="/employee/tasks"
              onSuccess={(data, resetForm) => {
                if (data) {
                  const newTask = {
                    content: data.name,
                    created_at: data.created_at,
                    id: data.id,
                    organization_id: null,
                    scheduled_time: data.scheduled_time,
                    status: data.status,
                    type: "task",
                    unique_id: "",
                    updated_at: data.updated_at,
                    user_id: data.creator_id,
                    user_name: data.creator && data.creator.name,
                    service: data.service,
                  };
                  setMessages((prevMessages) => [...prevMessages, newTask]);
                  setTimeout(() => {
                    const chatContainer = chatContainerRef.current;
                    if (chatContainer) {
                      chatContainer.scrollTop = chatContainer.scrollHeight;
                    }
                  }, 100);
                }
                resetForm();
              }}
              onError={() => {}}
              params={{
                include: "histories,messages,creator",
                modelable_type: "lead",
                modelable_id: () => get(selected, "id"),
              }}
              fields={[
                {
                  name: "user_id",
                  onSubmitValue: (value) => get(value, "id"),
                },
                {
                  name: "service_id",
                  onSubmitValue: (value) => get(value, "id"),
                },
                {
                  name: "modelable_id",
                  onSubmitValue: () => get(selected, "id"),
                },
                {
                  name: "modelable_type",
                  value: "lead",
                },
                {
                  name: "name",
                },
                {
                  name: "description",
                },
                {
                  name: "scheduled_time",
                  required: true,
                  onSubmitValue: (value) => {
                    if (!value) return null;
                    const date = new Date(value);
                    const utcDate = new Date(
                      Date.UTC(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      ),
                    );
                    return utcDate.toISOString();
                  },
                },
              ]}
            >
              {({ values, isSubmitting }) => (
                <>
                  <TaskMessageForm
                    isSubmitting={isSubmitting}
                    leadId={get(selected, "id")}
                  />
                </>
              )}
            </EntityForm.Main>
          </div>
        )}
      </div>
    </div>
  );
};
export default Chat;
